import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiPublicPayment } from '../../services/public/payment.public.service';
import { authController } from '../../scripts/authentication.script';

import logo from '../../assets/themes/svg/logo-official.svg';

type ReturnState = 'polling' | 'paid' | 'pending' | 'failed' | 'cancelled' | 'error';

const MAX_POLLS = 5;
const POLL_INTERVAL_MS = 4000;
const REDIRECT_DELAY_S = 4;

const PaymentReturnPage: React.FunctionComponent = () => {
  const { lang } = StateLanguage();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ReturnState>('polling');
  const [canRetry, setCanRetry] = useState<boolean | null>(null); // null = loading
  const [paidAmount, setPaidAmount] = useState<number | undefined>(undefined);
  const [paidCurrency, setPaidCurrency] = useState<string | undefined>(undefined);
  const [invoiceNumber, setInvoiceNumber] = useState<string | undefined>(undefined);
  const [countdown, setCountdown] = useState<number | null>(null);
  const pollCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const redirectCancelledRef = useRef(false);

  const hasSession = authController();

  // Server sends ?cancelled=1 (digital-billing/src/clients/mypos/ipc-client.ts:75).
  // Check presence, not value — a provider might re-encode 1 as true/yes/on.
  const cancelled = searchParams.has('cancelled') || searchParams.get('status') === 'cancelled';

  const formatAmount = (amount?: number, currency?: string): string => {
    if (amount == null) return '';
    const cur = (currency ?? 'CHF').toUpperCase();
    return `${cur} ${amount.toFixed(2)}`;
  };

  const clearSubscriptionSnapshot = () => {
    try { sessionStorage.removeItem('da_subscription_snapshot'); } catch {}
  };

  const startRedirectCountdown = useCallback(() => {
    if (!hasSession || redirectCancelledRef.current) return;

    clearSubscriptionSnapshot();
    setCountdown(REDIRECT_DELAY_S);

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          if (!redirectCancelledRef.current) {
            window.location.href = '/main/admin/subscription';
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [hasSession]);

  const cancelRedirect = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    redirectCancelledRef.current = true;
    setCountdown(null);
  }, []);

  const pollStatus = useCallback(async () => {
    if (!token) {
      setState('error');
      return;
    }

    try {
      const response = await ApiPublicPayment.getPaymentStatus(token);
      if (response.status === 200 && response.data) {
        const status = response.data.status;

        // Capture payment details from the response
        if (response.data.amount != null) setPaidAmount(response.data.amount);
        if (response.data.currency) setPaidCurrency(response.data.currency);
        if (response.data.invoice_number) setInvoiceNumber(response.data.invoice_number);

        if (status === 'paid' || status === 'succeeded') {
          setState('paid');
          return;
        }
        if (status === 'failed' || status === 'refused') {
          setState('failed');
          return;
        }
      } else {
        setState('error');
        return;
      }
    } catch {
      setState('error');
      return;
    }

    // Still pending — retry or give up
    pollCount.current += 1;
    if (pollCount.current >= MAX_POLLS) {
      setState('pending');
      return;
    }

    timerRef.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
  }, [token]);

  // Start redirect countdown when state becomes 'paid' and user has a session
  useEffect(() => {
    if (state === 'paid' && hasSession) {
      startRedirectCountdown();
    }
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [state, hasSession, startRedirectCountdown]);

  // Cancel redirect on any click anywhere on the page
  useEffect(() => {
    if (countdown === null) return;

    const handleClick = () => cancelRedirect();
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [countdown, cancelRedirect]);

  useEffect(() => {
    if (cancelled) {
      setState('cancelled');
      // Check if the token allows a retry
      if (token) {
        ApiPublicPayment.getPaymentStatus(token).then(response => {
          if (response.status === 200 && response.data?.can_retry) {
            setCanRetry(true);
          } else {
            setCanRetry(false);
          }
        }).catch(() => { setCanRetry(false); });
      } else {
        setCanRetry(false);
      }
      return;
    }

    pollStatus();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [cancelled, pollStatus, token]);

  const handleCheckAgain = async () => {
    if (!token) return;
    setState('polling');
    pollCount.current = 0;
    pollStatus();
  };

  const handleGoToSubscription = () => {
    cancelRedirect();
    clearSubscriptionSnapshot();
    window.location.href = '/main/admin/subscription';
  };

  const iconForState = (): React.ReactNode => {
    switch (state) {
      case 'paid':
        return <i className="bi bi-check-circle-fill text-success fs-3x mb-5"></i>;
      case 'pending':
      case 'polling':
        return <span className="spinner-border text-primary mb-5" style={{ width: 48, height: 48 }}></span>;
      case 'failed':
      case 'error':
        return <i className="bi bi-x-circle-fill text-danger fs-3x mb-5"></i>;
      case 'cancelled':
        return <i className="bi bi-dash-circle-fill text-warning fs-3x mb-5"></i>;
      default:
        return null;
    }
  };

  const titleForState = (): string => {
    switch (state) {
      case 'polling': return lang.labels.paymentProcessing;
      case 'paid': return lang.labels.paymentSucceeded;
      case 'pending': return lang.labels.paymentPending;
      case 'failed': return lang.labels.paymentFailed;
      case 'cancelled': return lang.labels.paymentCancelled;
      case 'error': return lang.labels.paymentStatusUnavailable;
      default: return '';
    }
  };

  const descriptionForState = (): string => {
    switch (state) {
      case 'polling': return lang.labels.paymentProcessingHint;
      case 'paid': return hasSession ? lang.labels.paymentSucceededHint : lang.labels.paymentNothingElseRequired;
      case 'pending': return lang.labels.paymentPendingHint;
      case 'failed': return lang.labels.paymentFailedHint;
      case 'cancelled': return lang.labels.paymentCancelledHint;
      case 'error': return lang.labels.paymentStatusUnavailableHint;
      default: return '';
    }
  };

  const renderPaymentDetails = () => {
    if (paidAmount == null && !invoiceNumber) return null;

    return (
      <table className="table gs-0 gy-2 m-0 text-start mx-auto mb-5" style={{ maxWidth: 300 }}>
        <tbody>
          {invoiceNumber && (
            <tr>
              <td className="fw-bold text-muted">{lang.labels.paymentInvoiceNumber}:</td>
              <td className="fw-bold fs-6">{invoiceNumber}</td>
            </tr>
          )}
          {paidAmount != null && (
            <tr>
              <td className="fw-bold text-muted">{lang.labels.paymentAmountPaid}:</td>
              <td className="fw-bolder fs-4">{formatAmount(paidAmount, paidCurrency)}</td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  return (
    <div className="d-flex flex-column flex-root h-100">
      <div className="d-flex flex-column flex-column-fluid">
        <div className="d-flex flex-column flex-column-fluid text-center p-10">
          <div className="cursor-pointer mb-10">
            <img src={logo} alt={lang.labels.digitalAdmin} className="h-75px mb-5" />
          </div>

          <div className="card card-flush mx-auto" style={{ maxWidth: 500 }}>
            <div className="card-body py-15">
              <div className="d-flex flex-column align-items-center">
                {iconForState()}
                <h2 className="fw-bolder mb-3">{titleForState()}</h2>
                <p className="text-gray-600 fs-6 mw-400px">{descriptionForState()}</p>

                {/* Payment details — shown on paid and pending */}
                {(state === 'paid' || state === 'pending') && renderPaymentDetails()}

                {/* Paid state — session: redirect countdown + button */}
                {state === 'paid' && hasSession && (
                  <>
                    {countdown !== null && (
                      <p className="text-gray-500 fs-7 mb-3">
                        {lang.labels.paymentRedirectingIn.replace('{0}', String(countdown))}
                      </p>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary mt-2"
                      onClick={handleGoToSubscription}
                    >
                      {lang.labels.paymentBackToSubscription}
                    </button>
                  </>
                )}

                {/* Paid state — no session: sign-in link */}
                {state === 'paid' && !hasSession && (
                  <a href="/" className="btn btn-sm btn-light-primary mt-4">
                    {lang.labels.paymentSignIn}
                  </a>
                )}

                {/* Pending state — exits */}
                {state === 'pending' && (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm btn-light-primary mt-4"
                      onClick={handleCheckAgain}
                    >
                      {lang.labels.paymentCheckAgain}
                    </button>
                    {hasSession && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light mt-2"
                        onClick={handleGoToSubscription}
                      >
                        {lang.labels.paymentBackToSubscription}
                      </button>
                    )}
                    {!hasSession && (
                      <a href="/" className="btn btn-sm btn-light mt-2">
                        {lang.labels.paymentSignIn}
                      </a>
                    )}
                  </>
                )}

                {/* Cancelled state — unchanged */}
                {state === 'cancelled' && canRetry === true && token && (
                  <a href={`/pay/${token}`} className="btn btn-sm btn-primary mt-4">
                    {lang.labels.paymentRetry}
                  </a>
                )}
                {state === 'cancelled' && canRetry === false && (
                  <>
                    <p className="text-gray-500 fs-7 mt-2">{lang.labels.paymentRetryExpired}</p>
                    <a href="/main/admin/subscription/invoices" className="btn btn-sm btn-light-primary mt-2">
                      {lang.labels.billingInvoices}
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex flex-center flex-column-auto p-10">
          <div className="d-flex align-items-center fw-bold fs-6">
            <a href="https://digitaladmin.ch" className="text-muted text-hover-primary px-2">{lang.labels.about}</a>
            <a href="mailto:contact@digitaladmin.ch" className="text-muted text-hover-primary px-2">{lang.labels.contact}</a>
            <a href="mailto:support@digitaladmin.ch" className="text-muted text-hover-primary px-2">{lang.labels.support}</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReturnPage;
